import { CouponModel } from '@/http/models/Coupon'
import { toast } from 'sonner'

import { useState } from 'react'

import { CouponModel as CouponModelType } from '@/types'

import { useFetchCoupons } from './useFetchCoupons'

type DialogMode = 'create' | 'edit' | 'view'

export const useCouponsTable = () => {
  // Dialog state
  const [mode, setMode] = useState<DialogMode>('create')
  const [couponDialogOpen, setCouponDialogOpen] = useState(false)
  const [editCoupon, setEditCoupon] = useState<CouponModelType.Coupon | null>(null)

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [couponToDelete, setCouponToDelete] = useState<CouponModelType.Coupon | null>(null)

  // Loading states
  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Fetch coupons data
  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchCoupons({
      sort: null,
    })

  // Dialog handlers
  const openCreateDialog = () => {
    setMode('create')
    setEditCoupon(null)
    setCouponDialogOpen(true)
  }

  const openViewDialog = (coupon: CouponModelType.Coupon) => {
    setMode('view')
    setEditCoupon(coupon)
    setCouponDialogOpen(true)
  }

  const openEditDialog = (coupon: CouponModelType.Coupon) => {
    setMode('edit')
    setEditCoupon(coupon)
    setCouponDialogOpen(true)
  }

  const closeDialog = () => {
    setCouponDialogOpen(false)
    setEditCoupon(null)
  }

  // Delete handlers
  const handleDeleteClick = (coupon: CouponModelType.Coupon) => {
    setCouponToDelete(coupon)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!couponToDelete?._id) return

    setDeleteLoading(true)
    const response = await CouponModel.delete({
      params: { id: couponToDelete._id },
    })

    if (response.success) {
      toast.success('Coupon deleted successfully')
      setDeleteDialogOpen(false)
      setCouponToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete coupon')
    }
    setDeleteLoading(false)
  }

  // Status toggle handler
  const toggleStatus = async (coupon: CouponModelType.Coupon) => {
    const newStatus = coupon.status === 'active' ? 'inactive' : 'active'

    const response = await CouponModel.update({
      params: { id: coupon._id },
      body: { status: newStatus },
    })

    if (response.success) {
      toast.success('Coupon status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  // CRUD handlers
  const handleCreateCoupon = async (data: any) => {
    setCreateLoading(true)
    const response = await CouponModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('Coupon created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to create coupon')
    }
    return response.success
    setCreateLoading(false)
  }

  const handleUpdateCoupon = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await CouponModel.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Coupon updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update coupon')
    }
    setUpdateLoading(false)
  }

  // Computed values
  const stats = {
    totalCoupons: pagination.totalCount,
    activeCoupons: data.filter((c) => c.status === 'active').length,
    inactiveCoupons: data.filter((c) => c.status === 'inactive').length,
  }

  return {
    // Data
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    stats,

    // Dialog state
    mode,
    couponDialogOpen,
    editCoupon,

    // Delete dialog state
    deleteDialogOpen,
    couponToDelete,

    // Loading states
    createLoading,
    updateLoading,
    deleteLoading,

    // Pagination handlers
    setPage,
    setPageSize,
    refresh,

    // Dialog handlers
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,

    // Delete handlers
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,

    // CRUD handlers
    handleCreateCoupon,
    handleUpdateCoupon,
    toggleStatus,
  }
}
